import { useEffect, useRef, useState } from 'react';
import { motion } from 'motion/react';
import Link from 'next/link';
import Image from 'next/image';
import Head from 'next/head';
import Container from 'components/container';

import { PrimaryLink } from 'shared/styles/components';
import { useTheme } from 'shared/styles/theme';
import { LIBUM_DOCS_NEXUM, LIBUM_DOCS_POWERON_PRO, LIBUM_KEYWORDS, LIBUM_URL } from 'shared/constants/globals';

import {
  LandingActions,
  LandingAnnouncement,
  LandingBody,
  LandingColumn,
  LandingColumns,
  LandingContainer,
  LandingLogos,
  LandingRow,
} from './landing.styles';

import HOVER_ARROW from 'public/icons/hover_arrow.svg';
import SPARKLE from 'public/icons/sparkle.svg';

import ALTURA_CU_LOGO from 'public/logos/orgs/altura_cu.svg';
import BCU_LOGO from 'public/logos/orgs/bcu.svg';
import JH_LOGO from 'public/logos/orgs/jack_henry.svg';
import MAX_LOGO from 'public/logos/orgs/max_cu.svg';
import PIONEERA_FCU from 'public/logos/orgs/pioneera_fcu.svg';
import PEAK_CU_LOGO from 'public/logos/orgs/peak_cu.svg';

import FOURFRONT_CU from 'public/logos/orgs/4front_cu.svg';
import CENTURY_FCU from 'public/logos/orgs/century_fcu.svg';
import CHARTER_OAK_FCU from 'public/logos/orgs/charter_oak_fcu.svg';
import CONGRESSIONAL_FCU from 'public/logos/orgs/congressional_fcu.svg';
import CREDIT_HUMAN from 'public/logos/orgs/credit_human.svg';
import DATCU from 'public/logos/orgs/datcu.svg';
import EDUCATORS_CU from 'public/logos/orgs/educators_cu.svg';
import EECU from 'public/logos/orgs/eecu.svg';
import FAMILY_SAVINGS_CU from 'public/logos/orgs/family_savings_cu.svg';
import FIRST_COMMUNITY_CU from 'public/logos/orgs/first_community_cu.svg';
import FIRST_UNITED_CU from 'public/logos/orgs/first_united_cu.svg';
import FIRSTCITY_CU from 'public/logos/orgs/firstcity_cu.svg';
import FOOTHILL_CU from 'public/logos/orgs/foothill_cu.svg';
import GLOBAL_CU from 'public/logos/orgs/global_cu.svg';
import HIGHMARK_CU from 'public/logos/orgs/highmark_cu.svg';
import INTERRA_CU from 'public/logos/orgs/interra_cu.svg';
import KITSAP_CU from 'public/logos/orgs/kitsap_cu.svg';
import LGE_CU from 'public/logos/orgs/lge_cu.svg';
import MAGNIFI_FINANCIAL from 'public/logos/orgs/magnifi_financial.svg';
import MEMBERS_1ST_FCU from 'public/logos/orgs/members_1st_fcu.svg';
import NEBO_CU from 'public/logos/orgs/nebo_cu.svg';
import P1FCU from 'public/logos/orgs/p1fcu.svg';
import PEN_AIR_CU from 'public/logos/orgs/pen_air_cu.svg';
import PHILADELPHIA_FCU from 'public/logos/orgs/philadelphia_fcu.svg';
import QUORUM_FCU from 'public/logos/orgs/quorum_fcu.svg';
import RIZE_CU from 'public/logos/orgs/rize_cu.svg';
import ROBINS_FINANCIAL_CU from 'public/logos/orgs/robins_financial_cu.svg';
import SAFE_FCU from 'public/logos/orgs/safe_fcu.svg';
import SILVER_STATE_CU from 'public/logos/orgs/silver_state_cu.svg';
import TOPSIDE_FCU from 'public/logos/orgs/topside_fcu.svg';
import VALLEY_FIRST_CU from 'public/logos/orgs/valley_first_cu.svg';
import WILDFIRE_CU from 'public/logos/orgs/wildfire_cu.svg';

import APLUS_FCU from 'public/logos/orgs/aplus_fcu.svg';
import MUTUAL_CU from 'public/logos/orgs/mutual_cu.svg';
import RESOUND_CU from 'public/logos/orgs/resound_cu.svg';
import PUBLIX_EMPLOYEES_FCU from 'public/logos/orgs/publix_employees_fcu.svg';
import REDWOOD_CU from 'public/logos/orgs/redwood_cu.svg';
import HOPE_CU from 'public/logos/orgs/hope_cu.svg';

// Heights are normalized toward a uniform rendered area within the 80x40 logo slot
// (wider wordmarks get a smaller height, squarer marks a larger one) so no single logo
// dominates the wall. See scratchpad gen-slot-gallery for how these were derived.
const ALL_LOGOS = [
  // Existing / partner logos
  { Component: ALTURA_CU_LOGO, name: 'Altura CU', height: 21 },
  { Component: BCU_LOGO, name: 'BCU', height: 29 },
  { Component: JH_LOGO, name: 'Jack Henry', height: 15 },
  { Component: MAX_LOGO, name: 'MAX CU', height: 30 },
  { Component: PIONEERA_FCU, name: 'Pioneera FCU', height: 22 },
  { Component: PEAK_CU_LOGO, name: 'Peak Credit Union', height: 22 },
  // Customer logos (normalized monochrome SVGs)
  { Component: FOURFRONT_CU, name: '4Front CU', height: 22 },
  { Component: CENTURY_FCU, name: 'Century FCU', height: 24 },
  { Component: CHARTER_OAK_FCU, name: 'Charter Oak FCU', height: 16 },
  { Component: CONGRESSIONAL_FCU, name: 'Congressional FCU', height: 27 },
  { Component: CREDIT_HUMAN, name: 'Credit Human', height: 27 },
  { Component: DATCU, name: 'DATCU', height: 19 },
  { Component: EDUCATORS_CU, name: 'Educators CU', height: 27 },
  { Component: EECU, name: 'EECU', height: 16 },
  { Component: FAMILY_SAVINGS_CU, name: 'Family Savings CU', height: 24 },
  { Component: FIRST_COMMUNITY_CU, name: 'First Community CU', height: 15 },
  { Component: FIRST_UNITED_CU, name: '1st United CU', height: 22 },
  { Component: FIRSTCITY_CU, name: 'FirstCity CU', height: 22 },
  { Component: FOOTHILL_CU, name: 'Foothill CU', height: 20 },
  { Component: GLOBAL_CU, name: 'Global CU', height: 22 },
  { Component: HIGHMARK_CU, name: 'Highmark CU', height: 21 },
  { Component: INTERRA_CU, name: 'Interra CU', height: 24 },
  { Component: KITSAP_CU, name: 'Kitsap CU', height: 16 },
  { Component: LGE_CU, name: 'LGE Community CU', height: 15 },
  { Component: MAGNIFI_FINANCIAL, name: 'Magnifi Financial', height: 21 },
  { Component: MEMBERS_1ST_FCU, name: 'Members 1st FCU', height: 21 },
  { Component: NEBO_CU, name: 'Nebo CU', height: 23 },
  { Component: P1FCU, name: 'P1FCU', height: 15 },
  { Component: PEN_AIR_CU, name: 'Pen Air CU', height: 21 },
  { Component: PHILADELPHIA_FCU, name: 'Philadelphia FCU', height: 25 },
  { Component: QUORUM_FCU, name: 'Quorum FCU', height: 17 },
  { Component: RIZE_CU, name: 'Rize CU', height: 30 },
  { Component: ROBINS_FINANCIAL_CU, name: 'Robins Financial CU', height: 19 },
  { Component: SAFE_FCU, name: 'SAFE FCU', height: 30 },
  { Component: SILVER_STATE_CU, name: 'Silver State Schools CU', height: 23 },
  { Component: TOPSIDE_FCU, name: 'Topside FCU', height: 23 },
  { Component: VALLEY_FIRST_CU, name: 'Valley First CU', height: 20 },
  { Component: WILDFIRE_CU, name: 'Wildfire CU', height: 24 },
  { Component: APLUS_FCU, name: 'A+ Federal Credit Union', height: 30 },
  { Component: MUTUAL_CU, name: 'Mutual Credit Union', height: 21 },
  { Component: RESOUND_CU, name: 'Resound Credit Union', height: 18 },
  { Component: PUBLIX_EMPLOYEES_FCU, name: 'Publix Employees Federal Credit Union', height: 13 },
  { Component: REDWOOD_CU, name: 'Redwood Credit Union', height: 25 },
  { Component: HOPE_CU, name: 'Hope Credit Union', height: 16 },
];

const VISIBLE_LOGOS_COUNT = 4;
const ROTATION_INTERVAL = 2500; // milliseconds

const TYPING_WORDS = [
  'developers',
  'leaders',
  'innovators',
  'visionaries',
  'engineers',
  'designers',
  'ops teams',
  'lending teams',
  'frontline staff',
  'member services',
  'compliance teams',
  'data analysts',
  'product managers',
  'IT teams',
  'executives',
  'loan officers',
  'back office',
];
const TYPING_SPEED = 100; // ms per character
const PAUSE_BEFORE_TYPING = 1000; // pause before typing new word
const PAUSE_DURATION = 2000; // pause before deleting
const DELETE_SPEED = 50; // ms per character when deleting

const LANDING_IMAGES = {
  powerOnPro: {
    light: '/images/landing-poweron-pro-light.webp',
    dark: '/images/landing-poweron-pro-dark.webp',
  },
  glaci: {
    light: '/images/landing-glaci-light.webp',
    dark: '/images/landing-glaci-dark.webp',
  },
  nexum: {
    light: '/images/landing-nexum-light.webp',
    dark: '/images/landing-nexum-dark.webp',
  },
};

const ALL_LOGO_INDICES = Array.from({ length: ALL_LOGOS.length }, (_, i) => i);

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function Landing() {
  const { theme } = useTheme();
  const [logoIndices, setLogoIndices] = useState<number[]>([0, 1, 2, 3]);
  const [exitingSlots, setExitingSlots] = useState<Set<number>>(new Set());
  const [enteringSlots, setEnteringSlots] = useState<Set<number>>(new Set());
  // Mirrors logoIndices so the rotation timer reads the current hand without a stale closure and
  // can draw the next logo outside the state updater (updaters must stay side-effect free).
  const logoIndicesRef = useRef<number[]>([0, 1, 2, 3]);

  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [currentText, setCurrentText] = useState(TYPING_WORDS[0]);
  const [isDeleting, setIsDeleting] = useState(false);

  const typingContentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number | 'auto'>('auto');

  // Measure content height when text changes
  useEffect(() => {
    if (typingContentRef.current) {
      setContentHeight(typingContentRef.current.offsetHeight);
    }
  }, [currentText]);

  // Typing animation effect
  useEffect(() => {
    const currentWord = TYPING_WORDS[currentWordIndex];

    const handleTyping = () => {
      if (!isDeleting) {
        // Typing
        if (currentText.length < currentWord.length) {
          setCurrentText(currentWord.substring(0, currentText.length + 1));
        } else {
          // Finished typing, pause then start deleting
          setTimeout(() => setIsDeleting(true), PAUSE_DURATION);
          return;
        }
      } else {
        // Deleting
        if (currentText.length > 0) {
          setCurrentText(currentWord.substring(0, currentText.length - 1));
        } else {
          // Finished deleting, move to random word (not the same as current)
          setIsDeleting(false);
          setCurrentWordIndex((prev) => {
            let nextIndex;
            do {
              nextIndex = Math.floor(Math.random() * TYPING_WORDS.length);
            } while (nextIndex === prev && TYPING_WORDS.length > 1);
            return nextIndex;
          });
          return;
        }
      }
    };

    // Determine delay: pause before typing if starting new word, otherwise use typing/delete speed
    const delay =
      !isDeleting && currentText.length === 0 ? PAUSE_BEFORE_TYPING : isDeleting ? DELETE_SPEED : TYPING_SPEED;

    const timeout = setTimeout(handleTyping, delay);

    return () => clearTimeout(timeout);
  }, [currentText, isDeleting, currentWordIndex]);

  // Logo rotation effect
  useEffect(() => {
    // Shuffled "deck" of logos not yet shown in the current cycle. Drawing from it guarantees
    // every logo appears once before any repeats; the deck refills (excluding whatever is on
    // screen, so nothing repeats immediately) once it runs out.
    let deck = shuffle(ALL_LOGO_INDICES);

    // Initialize with the first hand after mount to avoid a hydration mismatch
    const initialHand = deck.splice(0, VISIBLE_LOGOS_COUNT);
    logoIndicesRef.current = initialHand;
    setLogoIndices(initialHand);

    const drawNext = (visible: number[]): number => {
      if (deck.length === 0) deck = shuffle(ALL_LOGO_INDICES.filter((i) => !visible.includes(i)));
      // Skip any logo currently on screen so the visible set never duplicates
      let pos = deck.findIndex((i) => !visible.includes(i));
      if (pos === -1) {
        deck = shuffle(ALL_LOGO_INDICES.filter((i) => !visible.includes(i)));
        pos = 0;
      }
      return deck.splice(pos, 1)[0];
    };

    let lastSlot = -1;

    const interval = setInterval(() => {
      // Pick a random slot to update, but not the same as last time
      let nextSlot;
      do {
        nextSlot = Math.floor(Math.random() * VISIBLE_LOGOS_COUNT);
      } while (nextSlot === lastSlot);
      lastSlot = nextSlot;

      // Mark slot as exiting
      setExitingSlots((prev) => new Set(prev).add(nextSlot));

      // After exit animation, draw the next unshown logo and mark the slot as entering
      setTimeout(() => {
        // Draw outside the state updater so the deck mutation runs exactly once — React Strict
        // Mode invokes updaters twice in dev, which would otherwise splice two logos per tick.
        const currentIndices = logoIndicesRef.current;
        const newIndices = [...currentIndices];
        newIndices[nextSlot] = drawNext(currentIndices);
        logoIndicesRef.current = newIndices;
        setLogoIndices(newIndices);

        // Remove exiting state and add entering state
        setExitingSlots((prev) => {
          const newSet = new Set(prev);
          newSet.delete(nextSlot);
          return newSet;
        });

        setEnteringSlots((prev) => new Set(prev).add(nextSlot));

        // Remove entering state after animation completes
        setTimeout(() => {
          setEnteringSlots((prev) => {
            const newSet = new Set(prev);
            newSet.delete(nextSlot);
            return newSet;
          });
        }, 400);
      }, 400); // Exit animation duration
    }, ROTATION_INTERVAL);

    return () => {
      clearInterval(interval);
    };
  }, []);
  const title = 'Libum | Credit Union Software for Jack Henry Symitar';
  const description =
    'Libum builds credit union software for Jack Henry Symitar teams, including PowerOn Pro, PowerOn Pipelines, NexumAPI, and Glaci.';

  return (
    <>
      <Head>
        <link
          rel="preload"
          as="image"
          href={LANDING_IMAGES.powerOnPro.light}
          type="image/webp"
          media="(prefers-color-scheme: light)"
        />
        <link
          rel="preload"
          as="image"
          href={LANDING_IMAGES.powerOnPro.dark}
          type="image/webp"
          media="(prefers-color-scheme: dark)"
        />
      </Head>
      <Container
        title={title}
        description={description}
        keywords={LIBUM_KEYWORDS}
        url={`${LIBUM_URL}/`}
        socialCard="libum"
      >
        <LandingContainer>
          <LandingBody>
            <LandingColumns className="ltr">
              <LandingColumn className="left">
                <div className="image-container composite">
                  <Link href="/poweron-pro" className="main-image-link">
                    <Image
                      className="poweron-pro"
                      src={theme === 'dark' ? LANDING_IMAGES.powerOnPro.dark : LANDING_IMAGES.powerOnPro.light}
                      alt="PowerOn Pro in Visual Studio Code with a PowerOn file open"
                      height={357}
                      width={580}
                      priority={true}
                      unoptimized
                      style={{ borderRadius: '8px' }}
                    />
                  </Link>
                  <Link href="/glaci" className="overlay-image glaci">
                    <Image
                      src={theme === 'dark' ? LANDING_IMAGES.glaci.dark : LANDING_IMAGES.glaci.light}
                      alt="Glaci Data Analytics Dashboard"
                      width={151}
                      height={267}
                      loading="eager"
                      fetchPriority="low"
                      unoptimized
                      style={{ borderRadius: '8px' }}
                    />
                  </Link>
                  <Link href="/nexum" className="overlay-image nexum">
                    <Image
                      src={theme === 'dark' ? LANDING_IMAGES.nexum.dark : LANDING_IMAGES.nexum.light}
                      alt="NexumAPI JSON/REST Interface Documentation"
                      width={300}
                      height={162}
                      loading="eager"
                      fetchPriority="low"
                      unoptimized
                      style={{ borderRadius: '8px' }}
                    />
                  </Link>
                  <div className="dot-mask"></div>
                </div>
              </LandingColumn>
              <LandingColumn className="right poweron-pro">
                <LandingRow>
                  <h1>
                    <span>Building CU software</span>
                    <motion.div
                      className="typing-line"
                      animate={{ height: contentHeight }}
                      transition={{ duration: 0.25, ease: 'easeOut' }}
                    >
                      <div ref={typingContentRef} className="typing-content">
                        for <span className="accent">&gt;</span> <span className="typing-text">{currentText}</span>
                      </div>
                    </motion.div>
                  </h1>
                  <p>Modern apps, tools, and APIs that make the Jack Henry core work better for your credit union.</p>
                </LandingRow>
                <LandingRow>
                  <Link href="/products">
                    <PrimaryLink className="landing-button">View Products</PrimaryLink>
                  </Link>
                </LandingRow>
                <LandingRow>
                  <LandingAnnouncement>
                    <span className="tag ai">
                      <SPARKLE />
                      <span className="label">New</span>
                    </span>
                    <Link href="/blog/symitar-core-speaks-ai">
                      Check out our AI tooling for NexumAPI
                      <HOVER_ARROW />
                    </Link>
                  </LandingAnnouncement>
                  <LandingActions>
                    <Link href={`${LIBUM_DOCS_POWERON_PRO}/quickstart`} target="_blank">
                      Write PowerOn in VS Code
                    </Link>
                    <Link href={`${LIBUM_DOCS_NEXUM}/mcp`} target="_blank">
                      Use AI with SymX
                    </Link>
                  </LandingActions>
                  <LandingLogos>
                    <p>Loved by hundreds of teams</p>
                    <div className="logo-wall">
                      {logoIndices.map((logoIndex, slotIndex) => {
                        const logoData = ALL_LOGOS[logoIndex];
                        const Logo = logoData.Component;
                        const isExiting = exitingSlots.has(slotIndex);
                        const isEntering = enteringSlots.has(slotIndex);
                        return (
                          <div key={slotIndex} className="logo-slot">
                            <div
                              className={`logo-item ${isExiting ? 'exiting' : ''} ${isEntering ? 'entering' : ''}`}
                            >
                              <Logo role="img" aria-label={logoData.name} style={{ height: `${logoData.height}px` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </LandingLogos>
                </LandingRow>
              </LandingColumn>
            </LandingColumns>
          </LandingBody>
        </LandingContainer>
      </Container>
    </>
  );
}
